import { CookieRadarResult } from '@dieterApi/scan/types';
import type { TFunction } from 'react-i18next';

// Shared types, parsing and coverage computation for the cookie-scanner result
// views (inline row display, report route, print route, score tooltip). All
// consumers must derive "covered vs. missing in DSE" from here so the score
// tooltip, the dashboard detail view and the customer-facing report can never
// disagree about the same scan.

export interface MatchEvidence {
  source?: string;
  matched_by?: string;
  snippet?: string;
}

export interface CookieEvidence extends MatchEvidence {
  name: string;
}

export interface TpEvidence extends MatchEvidence {
  domain: string;
}

export interface SecurityHeaderResult {
  name: string;
  rating: 'ok' | 'warn' | 'bad';
  value: string | null;
  notes: string[];
}

export interface SecurityHeadersData {
  url: string;
  grade: string;
  https: boolean;
  headers: SecurityHeaderResult[];
  raw_headers?: Record<string, string>;
}

export interface ScanProfileData {
  depth?: 'quick' | 'standard' | 'deep';
  scanned_pages?: number;
  consent_states?: string[];
  dse_source?: 'manual' | 'discovered' | 'none';
}

export interface DseMatchesDataFull {
  cookies: string[];
  generic_cookies: string[];
  tps: string[];
  technically_necessary_cookies: string[];
  technically_necessary_tps: string[];
  cookie_evidence: CookieEvidence[];
  tp_evidence: TpEvidence[];
  cookie_providers: Record<string, string>;
  tp_providers: Record<string, string>;
  security_headers?: SecurityHeadersData;
  scan_profile?: ScanProfileData;
  site_unreachable?: boolean;
}

export const SECURITY_HEADER_HELP_KEY: Record<string, string> = {
  'Strict-Transport-Security': 'hsts',
  'Content-Security-Policy': 'csp',
  'X-Frame-Options': 'xfo',
  'X-Content-Type-Options': 'xcto',
  'Referrer-Policy': 'rp',
  'Permissions-Policy': 'pp',
};

export const EMPTY_DSE_MATCHES: DseMatchesDataFull = {
  cookies: [],
  generic_cookies: [],
  tps: [],
  technically_necessary_cookies: [],
  technically_necessary_tps: [],
  cookie_evidence: [],
  tp_evidence: [],
  cookie_providers: {},
  tp_providers: {},
};

function normalizeStringMap(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {};
  const normalized: Record<string, string> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([k, v]) => {
    if (typeof v === 'string' && v.trim()) normalized[k.toLowerCase()] = v.trim();
  });
  return normalized;
}

const toStringArray = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

/**
 * Parse the scanner's `dse_matches` payload, which the backend may deliver as
 * an object or a JSON string. Never throws; unusable input yields
 * EMPTY_DSE_MATCHES.
 */
export function parseDseMatches(raw: unknown): DseMatchesDataFull {
  try {
    let obj: Record<string, unknown> | null = null;
    if (typeof raw === 'string' && raw.trim()) {
      obj = JSON.parse(raw) as Record<string, unknown>;
    } else if (raw && typeof raw === 'object') {
      obj = raw as Record<string, unknown>;
    }
    if (!obj) return EMPTY_DSE_MATCHES;
    return {
      cookies: toStringArray(obj.cookies),
      generic_cookies: toStringArray(obj.generic_cookies),
      tps: toStringArray(obj.tps),
      technically_necessary_cookies: toStringArray(obj.technically_necessary_cookies),
      technically_necessary_tps: toStringArray(obj.technically_necessary_tps),
      cookie_evidence: Array.isArray(obj.cookie_evidence) ? (obj.cookie_evidence as CookieEvidence[]) : [],
      tp_evidence: Array.isArray(obj.tp_evidence) ? (obj.tp_evidence as TpEvidence[]) : [],
      cookie_providers: normalizeStringMap(obj.cookie_providers),
      tp_providers: normalizeStringMap(obj.tp_providers),
      security_headers: obj.security_headers as SecurityHeadersData | undefined,
      scan_profile: obj.scan_profile as ScanProfileData | undefined,
      site_unreachable: obj.site_unreachable === true,
    };
  } catch {
    return EMPTY_DSE_MATCHES;
  }
}

export interface ConsentStateLike {
  cookies?: { name?: string | null }[] | null;
  third_parties?: { domain?: string | null }[] | null;
}

export type ConsentStatesLike = Partial<
  Record<'pre_consent' | 'post_accept' | 'post_reject', ConsentStateLike | null | undefined>
>;

export interface ScanCoverage {
  dseCookieNames: Set<string>;
  genericCoveredCookieNames: Set<string>;
  technicallyNecessaryCookieNames: Set<string>;
  dseTpDomains: Set<string>;
  technicallyNecessaryTpDomains: Set<string>;
  /** All unique cookie names across the three consent states (lowercased). */
  allCookieNames: Set<string>;
  /** All unique third-party domains across the three consent states (lowercased). */
  allTpDomains: Set<string>;
  /** Cookies counting as a score deduction (not in DSE, not generic, not technically necessary). */
  cookiesNotInDse: string[];
  /** Third-party domains counting as a score deduction (not in DSE, not technically necessary). */
  tpsNotInDse: string[];
  totalDeductions: number;
}

/**
 * Single source of truth for "which cookies / third parties are (not) covered
 * by the privacy policy". Mirrors the scanner's score deduction logic.
 */
export function computeCoverage(dseMatches: DseMatchesDataFull, states: ConsentStatesLike | undefined): ScanCoverage {
  const dseCookieNames = new Set(dseMatches.cookies.map((c) => c.toLowerCase()));
  const genericCoveredCookieNames = new Set(dseMatches.generic_cookies.map((c) => c.toLowerCase()));
  const technicallyNecessaryCookieNames = new Set(dseMatches.technically_necessary_cookies.map((c) => c.toLowerCase()));
  const dseTpDomains = new Set(dseMatches.tps.map((d) => d.toLowerCase()));
  const technicallyNecessaryTpDomains = new Set(dseMatches.technically_necessary_tps.map((d) => d.toLowerCase()));

  const allCookieNames = new Set<string>();
  const allTpDomains = new Set<string>();
  (['pre_consent', 'post_accept', 'post_reject'] as const).forEach((state) => {
    states?.[state]?.cookies?.forEach((c) => {
      const name = (c?.name ?? '').toLowerCase();
      if (name) allCookieNames.add(name);
    });
    states?.[state]?.third_parties?.forEach((tp) => {
      const domain = (tp?.domain ?? '').toLowerCase();
      if (domain) allTpDomains.add(domain);
    });
  });

  const cookiesNotInDse = [...allCookieNames].filter(
    (name) => !dseCookieNames.has(name) && !genericCoveredCookieNames.has(name) && !technicallyNecessaryCookieNames.has(name),
  );
  const tpsNotInDse = [...allTpDomains].filter(
    (d) => !dseTpDomains.has(d) && !technicallyNecessaryTpDomains.has(d),
  );

  return {
    dseCookieNames,
    genericCoveredCookieNames,
    technicallyNecessaryCookieNames,
    dseTpDomains,
    technicallyNecessaryTpDomains,
    allCookieNames,
    allTpDomains,
    cookiesNotInDse,
    tpsNotInDse,
    totalDeductions: cookiesNotInDse.length + tpsNotInDse.length,
  };
}

export function getDisplaySiteTitle(url: string): string {
  try {
    return new URL(url).hostname || url;
  } catch {
    return url;
  }
}

const SCORE_COLORS: Record<string, string> = {
  A: 'bg-green-500 text-white',
  B: 'bg-teal-500 text-white',
  C: 'bg-yellow-500 text-white',
  D: 'bg-orange-500 text-white',
  E: 'bg-red-500 text-white',
  F: 'bg-red-700 text-white',
};

export function ScoreMini({ score }: { score: string | null | undefined }) {
  if (!score) return null;
  const key = score.toUpperCase();
  const cls = SCORE_COLORS[key] ?? 'bg-gray-400 text-white';
  return (
    <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-xs font-bold shrink-0 ${cls}`}>
      {key}
    </span>
  );
}

/**
 * One-line explanation for the score badge hover. `dpsUrl` is the
 * user-provided privacy policy URL from the scan record — a manually provided
 * DSE counts as found even when the scanner discovered none itself.
 */
export function buildScoreTooltip(
  parsed: CookieRadarResult | null,
  dpsUrl: string | null | undefined,
  t: TFunction,
): string | null {
  if (!parsed?.site?.privacy_score) return null;
  const siteAny = parsed.site as Record<string, unknown>;
  const dseMatches = parseDseMatches(siteAny['dse_matches']);
  if (dseMatches.site_unreachable) return t('route.scanresult.tooltip.site_unreachable');
  const dseFound = !!((dpsUrl ?? '').trim() || (siteAny['dse_found_url'] as string));
  if (!dseFound) return t('route.scanresult.tooltip.no_dse');

  const { cookiesNotInDse, tpsNotInDse } = computeCoverage(dseMatches, parsed.states);
  const undocCookies = cookiesNotInDse.length;
  const undocTps = tpsNotInDse.length;
  if (undocCookies === 0 && undocTps === 0) return t('route.scanresult.tooltip.all_documented');
  if (undocCookies > 0 && undocTps > 0) {
    return t('route.scanresult.tooltip.missing_both', {
      cookies: t('route.scanresult.tooltip.part_cookies', { count: undocCookies }),
      tps: t('route.scanresult.tooltip.part_tps', { count: undocTps }),
    });
  }
  return undocCookies > 0
    ? t('route.scanresult.tooltip.missing_cookies', { count: undocCookies })
    : t('route.scanresult.tooltip.missing_tps', { count: undocTps });
}
